import apiClient from "./apiClient";

const R = "/api/reports";

// The backend expects fromDate/toDate (confirmed against the live swagger spec at
// https://dcm-api.ezyerp.biz/swagger/index.html) — components use from/to, so translate here.
// toDate gets an end-of-day time: the backend treats a bare date as T00:00:00 (matching
// how invoiceService stores invoiceDate), so a same-day from/to range would otherwise
// collapse to a zero-width [T00:00:00, T00:00:00) window and return no rows.
const dateParams = ({ from, to, ...rest }) => ({
  ...rest,
  ...(from ? { fromDate: `${from}T00:00:00` } : {}),
  ...(to ? { toDate: `${to}T23:59:59` } : {}),
});

export const getAppointmentReport = (params) => apiClient.get(`${R}/appointments`, { params: dateParams(params) });
// Sales report only supports patientId (not doctorId) per the API.
export const getSalesReport = (params) => apiClient.get(`${R}/sales`, { params: dateParams(params) });
export const getDailyCollection = (params) => apiClient.get(`${R}/daily-collection`, { params });
export const getPurchaseReport = (params) => apiClient.get(`${R}/purchases`, { params: dateParams(params) });
// API param is `accountId`, not `accountHeadId`.
export const getExpenseReport = ({ accountHeadId, ...rest }) =>
  apiClient.get(`${R}/expenses`, { params: dateParams({ ...rest, ...(accountHeadId ? { accountId: accountHeadId } : {}) }) });
export const getCurrentStockReport = (params) => apiClient.get(`${R}/stock`, { params });
export const getLowStockReport = (params) => apiClient.get(`${R}/low-stock`, { params });
// API only accepts productId for stock movement — no date filtering server-side.
export const getStockMovementReport = (params) => apiClient.get(`${R}/stock-movement`, { params });
export const getStockAdjustmentReport = (params) => apiClient.get(`${R}/stock-adjustments`, { params });
export const getCustomerStatement = (params) => apiClient.get(`${R}/customer-statement`, { params: dateParams(params) });
export const getSupplierStatement = (params) => apiClient.get(`${R}/supplier-statement`, { params: dateParams(params) });
// API accepts no query params at all for receivables/payables — always returns the full current snapshot.
export const getReceivables = () => apiClient.get(`${R}/receivables`);
export const getPayables = () => apiClient.get(`${R}/payables`);
export const getMISReport = (params) => apiClient.get(`${R}/mis`, { params: dateParams(params) });

// Patient/treatment-wise Daily Report — one row per invoice generated on `date`
// (defaults to today server-side if omitted). Not a date-range endpoint, so it
// doesn't go through dateParams — the param is a bare `date`, not fromDate/toDate.
export const getDailyPatientReport = (date) => apiClient.get(`${R}/daily-patient`, { params: date ? { date } : {} });

// Combined Expense + Journal + Supplier Payment + Supplier Advance report.
export const getDailyExpenseReport = (params) => apiClient.get(`${R}/daily-expenses`, { params: dateParams(params) });

// Per-bank running ledger, seeded from that bank's openingBalance. `bankId` is required.
export const getBankLedgerReport = ({ bankId, ...rest }) =>
  apiClient.get(`${R}/bank-ledger`, { params: { bankId, ...dateParams(rest) } });

// Cash running ledger — same shape as bank-ledger minus the bankId param, since no
// transaction carries a cashID (there's a single cash pool, see cashService.js). Seeded
// from the summed openingBalance of the Active Cash Master rows.
export const getCashLedgerReport = (params) => apiClient.get(`${R}/cash-ledger`, { params: dateParams(params) });
